import React, { useRef } from 'react';
import { Button } from 'antd';
import { FileDoneOutlined } from '@ant-design/icons';
import { VehicleServiceKpi } from '../../store/analytics.store';
import useGeneratePDF from '../../hooks/useGeneratePDF';
import '../../styles/reports.scss';

interface Route {
  trip_id: number;
  vehicle_capacity: number;
  locations_capacity: number;
}

interface AnalyticsSummaryReportProps {
  metrics: {
    carbon_footprint: number;
    carbon_foot_print_if_unoptimized: number;
    carbon_savings: number;
    distance_saved: number;
    fuel_consumption: number;
    fuel_saving: number;
    optimized_distance: number;
    total_distance: number;
    total_fuel_consumption: number;
    total_actual_distance: number;
  };
  locations: {
    number_of_locations: number;
    number_of_routes: number;
  };
  routeOverviewMetrics: {
    delivery_success_rate: string;
    total_actual_deliveries: string;
    total_cancelled_deliveries: number;
    total_planned_deliveries: string;
    total_skipped_deliveries: number;
  };
  vehicleServiceKpi: VehicleServiceKpi | null;
  routes: Route[];
  // eslint-disable-next-line react/require-default-props
  dateRange?: { start?: string; end?: string };
}

interface TemplateProps extends AnalyticsSummaryReportProps {
  templateRef: React.RefObject<HTMLDivElement>;
}

const AnalyticsSummaryTemplate = ({
  templateRef,
  metrics,
  locations,
  routeOverviewMetrics,
  routes,
  vehicleServiceKpi,
  dateRange,
}: TemplateProps) => {
  const dateLabel = dateRange?.start && dateRange?.end ? `${dateRange.start} to ${dateRange.end}` : 'All Dates';

  const totalOptimumCapacity = routes.reduce((sum, r) => sum + (r.vehicle_capacity || 0), 0);
  const totalUtilizedCapacity = routes.reduce((sum, r) => sum + (r.locations_capacity || 0), 0);
  const capacityPct =
    totalOptimumCapacity > 0 ? ((totalUtilizedCapacity / totalOptimumCapacity) * 100).toFixed(2) : '0';
  const avgLocPerRoute = locations.number_of_locations / locations.number_of_routes;
  const avgDisPerRoute = metrics.total_distance / locations.number_of_routes;
  const avgDisBetweenLocs = metrics.total_distance / locations.number_of_locations;
  const fuelEffPct =
    metrics.fuel_consumption > 0 ? ((metrics.fuel_saving / metrics.fuel_consumption) * 100).toFixed(2) : '0';
  const distanceOptPct =
    metrics.total_distance > 0 ? ((metrics.distance_saved / metrics.total_distance) * 100).toFixed(2) : '0';
  const routeAdherence = (() => {
    if (!metrics.total_actual_distance || !metrics.optimized_distance || metrics.optimized_distance === 0) return 'N/A';
    const val = Math.min(1, 2 - metrics.total_actual_distance / metrics.optimized_distance) * 100;
    return val < 0 ? '0.00%' : `${val.toFixed(2)}%`;
  })();

  const sections: { title: string; color: string; bg: string; items: [string, string][] }[] = [
    {
      title: 'Overview',
      color: '#1a6eb5',
      bg: '#dbeafe',
      items: [
        ['Date Range', dateLabel],
        ['Total Routes', String(locations.number_of_routes)],
        ['Total Locations', String(locations.number_of_locations)],
        ['Route Adherence', routeAdherence],
      ],
    },
    {
      title: 'Distance Metrics',
      color: '#16a085',
      bg: '#d1f2eb',
      items: [
        ['Total Distance', `${(metrics.total_distance || 0).toFixed(2)} Km`],
        ['Total Actual Distance', `${(metrics.total_actual_distance || 0).toFixed(2)} Km`],
        ['Optimized Distance', `${(metrics.optimized_distance || 0).toFixed(2)} Km`],
        ['Distance Saved', `${(metrics.distance_saved || 0).toFixed(2)} Km`],
        ['Distance Optimization', `${distanceOptPct}%`],
        ['Avg Distance per Route', `${Number.isNaN(avgDisPerRoute) ? '0' : avgDisPerRoute.toFixed(2)} Km`],
        ['Avg Distance btw Locs', `${Number.isNaN(avgDisBetweenLocs) ? '0' : avgDisBetweenLocs.toFixed(2)} Km`],
        ['Avg Locations per Route', `${Number.isNaN(avgLocPerRoute) ? '0' : avgLocPerRoute.toFixed(0)}`],
      ],
    },
    {
      title: 'Fuel Metrics',
      color: '#d35400',
      bg: '#fde8d8',
      items: [
        ['Fuel Consumption', `${(metrics.fuel_consumption || 0).toFixed(2)} L`],
        ['Total Fuel Consumption', `${(metrics.total_fuel_consumption || 0).toFixed(2)} L`],
        ['Fuel Saved', `${(metrics.fuel_saving || 0).toFixed(2)} L`],
        ['Fuel Efficiency', `${fuelEffPct}%`],
      ],
    },
    {
      title: 'Delivery Performance',
      color: '#7d3c98',
      bg: '#e8daef',
      items: [
        ['Total Planned Deliveries', routeOverviewMetrics.total_planned_deliveries],
        ['Total Actual Deliveries', routeOverviewMetrics.total_actual_deliveries],
        ['Total Cancelled', String(routeOverviewMetrics.total_cancelled_deliveries)],
        ['Total Skipped', String(routeOverviewMetrics.total_skipped_deliveries)],
        ['Overall Success Rate', `${(Number(routeOverviewMetrics.delivery_success_rate) * 100).toFixed(2)}%`],
      ],
    },
    {
      title: 'Capacity Utilization',
      color: '#b7950b',
      bg: '#fef9e7',
      items: [
        ['Total Capacity', `${totalOptimumCapacity.toFixed(2)} KG`],
        ['Total Utilized Capacity', `${totalUtilizedCapacity.toFixed(2)} KG`],
        ['Capacity Optimization', `${capacityPct}%`],
      ],
    },
    {
      title: 'Vehicle Service KPI',
      color: '#1b9d3b',
      bg: '#d0f0c0',
      items: [
        ['Service Completed Vehicles', String(vehicleServiceKpi?.service_ok_vehicles ?? 0)],
        ['Service OVERDUE Vehicles', String(vehicleServiceKpi?.service_overdue_vehicles ?? 0)],
        ['Service DUE SOON Vehicles', String(vehicleServiceKpi?.service_due_vehicles ?? 0)],
      ],
    },
  ];

  return (
    <div
      ref={templateRef}
      className="report report--w900"
      style={{ '--report-accent': '#2c3e50' } as React.CSSProperties}
    >
      <div className="report__header">
        <span className="report__title">Analytics Summary Report</span>
        <span className="report__generated">Generated: {new Date().toLocaleString()}</span>
      </div>

      {sections.map(({ title, color, bg, items }) => (
        <div key={title} style={{ marginBottom: 20 }}>
          <div className="report__section-title" style={{ '--section-color': color } as React.CSSProperties}>
            {title}
          </div>
          <div className="report__grid">
            {items.map(([label, value]) => (
              <div key={label} className="report__row">
                <div className="report__cell-label" style={{ '--section-bg': bg } as React.CSSProperties}>
                  {label}
                </div>
                <div className="report__cell-value">{value}</div>
              </div>
            ))}
          </div>
        </div>
      ))}

      <div className="report__footer">
        <span>RouteSonar — Analytics Summary Report</span>
        <span>{new Date().toLocaleDateString()}</span>
      </div>
    </div>
  );
};

AnalyticsSummaryTemplate.displayName = 'AnalyticsSummaryTemplate';

const AnalyticsSummaryReport = ({ dateRange, ...rest }: AnalyticsSummaryReportProps): JSX.Element => {
  const templateRef = useRef<HTMLDivElement>(null);
  const { generate, loading } = useGeneratePDF(templateRef, {
    fileName: `analytics-summary-report.pdf`,
    orientation: 'landscape',
    avoidRowCuts: true,
  });

  return (
    <>
      <AnalyticsSummaryTemplate templateRef={templateRef} dateRange={dateRange} {...rest} />
      <Button type="default" icon={<FileDoneOutlined />} loading={loading} onClick={generate}>
        {loading ? 'Generating...' : 'Analytics Summary'}
      </Button>
    </>
  );
};

AnalyticsSummaryReport.defaultProps = {
  dateRange: undefined,
};

export default AnalyticsSummaryReport;
